
<script setup>
// --- Validation logic ---
import { ref, watch, computed } from 'vue';
const phoneError = ref('');
const emailError = ref('');

function validatePhone(phone) {
  if (!phone) return '';
  return /^\+?[0-9\s()-]{6,}$/.test(phone) ? '' : 'Nur Zahlen und gültige Zeichen erlaubt.';
}
function validateEmail(email) {
  if (!email) return '';
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) ? '' : 'Ungültiges E-Mail Format.';
}

// Helper to check if a contact is complete (Vorname, Nachname, Email required)
function isContactComplete(contact) {
  if (!contact) return false;
  return !!(contact.firstName && contact.firstName.trim() && contact.lastName && contact.lastName.trim() && contact.email && contact.email.trim());
}

function validateInputs() {
  phoneError.value = validatePhone(props.currentContact?.phoneMobile);
  emailError.value = validateEmail(props.currentContact?.email);
  let required = props.editMode && !isExistingContact.value;
  let genderValid = true;
  let firstNameValid = true;
  let lastNameValid = true;
  let emailValid = !emailError.value;
  if (required) {
    genderValid = !!props.currentContact?.gender;
    firstNameValid = !!props.currentContact?.firstName && props.currentContact.firstName.trim() !== '';
    lastNameValid = !!props.currentContact?.lastName && props.currentContact.lastName.trim() !== '';
    emailValid = !!props.currentContact?.email && props.currentContact.email.trim() !== '' && !emailError.value;
  }
  return genderValid && firstNameValid && lastNameValid && emailValid && !phoneError.value;
}

const canSave = computed(() => {
  if (!props.editMode) return false;
  return validateInputs();
});

const props = defineProps({
  currentContact: { type: Object, required: false, default: null },
  contacts: { type: Array, required: true },
  currentContactIndex: { type: Number, required: true },
  editMode: { type: Boolean, required: true },
  onPrev: { type: Function, required: true },
  onNext: { type: Function, required: true },
  onAdd: { type: Function, required: true },
  onEdit: { type: Function, required: true },
  onSave: { type: Function, required: true },
  onUndo: { type: Function, required: true },
  copyInputValue: { type: Function, required: true }
});

const newestContactId = computed(() => {
  const list = Array.isArray(props.contacts) ? props.contacts : [];
  let latest = null;
  for (const c of list) {
    if (!c) continue;
    const dc = c.dateCreated ? new Date(c.dateCreated) : null;
    if (!latest) { latest = c && dc ? c : latest; continue; }
    const dl = latest?.dateCreated ? new Date(latest.dateCreated) : null;
    if (dc && (!dl || dc > dl)) latest = c;
  }
  return latest?.id ?? null;
});

// Bestehender Entscheider (mit persistenter ID) → Stammdatenfelder sperren
const isExistingContact = computed(() => {
  return !!(props.currentContact && props.currentContact.id);
});

// Anrede (gender) ist nur gesperrt, wenn:
// - nicht im Edit-Modus
// - es ein bestehender Kontakt (mit id) ist UND Vorname/Nachname leer
const isLockedGender = computed(() => {
  if (!props.editMode) return true;
  // Neuer Entscheider (ohne id): immer editierbar
  if (!isExistingContact.value) return false;
  // Bestehender Entscheider: nur sperren wenn Vorname oder Nachname leer
  if (!props.currentContact?.firstName || !props.currentContact?.lastName) return true;
  return false;
});
const isLockedFirstName = computed(() => {
  return !props.editMode || isExistingContact.value;
});
const isLockedLastName = computed(() => {
  // Nachname ist nur bei bestehendem Entscheider (mit id) gesperrt
  return isExistingContact.value;
});

// Vorname ist Pflichtfeld, wenn leer
const isGenderRequired = computed(() => props.editMode && !isExistingContact.value && (!props.currentContact?.gender));
const isFirstNameRequired = computed(() => props.editMode && !isExistingContact.value && (!props.currentContact?.firstName || props.currentContact.firstName.trim() === ''));
const isLastNameRequired = computed(() => props.editMode && !isExistingContact.value && (!props.currentContact?.lastName || props.currentContact.lastName.trim() === ''));
const isEmailRequired = computed(() => props.editMode && !isExistingContact.value && (!props.currentContact?.email || props.currentContact.email.trim() === ''));

function displayDate(val) {
  if (!val) return '-';
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString('de-DE');
  } catch(e) {
    return String(val);
  }
}

// Local loading spinner when actions take longer
const isBusy = ref(false);
async function runWithLoading(fn){
  try {
    isBusy.value = true;
    const res = fn?.();
    if (res && typeof res.then === 'function') await res;
  } finally {
    isBusy.value = false;
  }
}
const handlePrev = () => runWithLoading(props.onPrev);
const handleNext = () => runWithLoading(props.onNext);
const handleAdd = () => runWithLoading(props.onAdd);
const handleEdit = () => runWithLoading(props.onEdit);
const handleSave = () => runWithLoading(props.onSave);
const handleUndo = () => runWithLoading(props.onUndo);

watch(() => props.currentContact?.phoneMobile, val => { phoneError.value = validatePhone(val); });
watch(() => props.currentContact?.email, val => { emailError.value = validateEmail(val); });
// Gender aus "null"-String in echtes null normalisieren
watch(
  () => props.currentContact && props.currentContact.gender,
  (val) => {
    if (val === 'null' && props.currentContact) {
      props.currentContact.gender = null;
    }
  }
);
</script>

<template>
  <div class="dm-card">
    <div v-if="isBusy" class="dm-loading"><span class="dm-loader" aria-label="Lädt"></span></div>
    <div class="dm-header">
      <div class="dm-title">
        <h4>Entscheider</h4>
        <span class="dm-count">
          <template v-if="contacts && contacts.length > 0 && currentContact">
            {{ currentContactIndex + 1 }} / {{ contacts.length }}
          </template>
          <template v-else>
            0 / 1
          </template>
        </span>
      </div>
      <div class="dm-actions">
        <template v-if="contacts && contacts.length > 1 && currentContact">
          <button class="icon-btn" :disabled="currentContactIndex === 0" @click="handlePrev" title="Vorheriger Entscheider">
            <img src="@/assets/imgs/icons/chevron-right-solid.svg" style="transform:rotate(180deg)" />
          </button>
          <button class="icon-btn" :disabled="currentContactIndex === contacts.length - 1" @click="handleNext" title="Nächster Entscheider">
            <img src="@/assets/imgs/icons/chevron-right-solid.svg" />
          </button>
          <div class="dm-actions-sep" aria-hidden="true"></div>
        </template>
          <button
            class="btn"
            @click="handleAdd"
            title="Neuen Entscheider hinzufügen"
            :disabled="editMode || isBusy"
            v-show="!editMode && !isBusy"
          >
            Hinzufügen
          </button>
        <button v-if="editMode" class="btn primary" @click="handleSave" name="editContactClose" title="Änderungen speichern" :disabled="!canSave">
          Speichern
        </button>
        <button v-if="editMode" class="btn subtle" @click="handleUndo" name="editContactUndo" title="Änderungen verwerfen">
          Abbrechen
        </button>
        <button class="btn" @click="handleEdit" name="editContact" title="Entscheider bearbeiten" v-if="!editMode && contacts && contacts.length > 0 && currentContact">Bearbeiten</button>
      </div>
      <span v-if="editMode && (!canSave)" class="input-error" style="display:block;margin-top:8px;max-width: max-content;">Bitte beheben Sie die Fehler in den Feldern.</span>
    </div>

    <div v-if="contacts && contacts.length > 0 && currentContact && editMode" class="contactInfoWrapper prominent-form editing-mode">
      <div class="dm-meta">
        <span v-if="currentContact?.id === newestContactId" class="dm-pill dm-new inline">Neu</span>
        <span v-if="currentContact?.main === true" class="dm-pill dm-main inline">Haupt</span>
        <span v-if="!currentContact?.gender || !currentContact?.firstName || !currentContact?.lastName || !currentContact?.email || !canSave" class="dm-pill dm-unvollstaendig inline">Unvollständig</span>
        <span class="dm-date" v-if="currentContact?.dateCreated">Erstellt: {{ displayDate(currentContact.dateCreated) }}</span>
      </div>
      <div class="input-wrapper threeColumns">
        <div class="inputRow">
          <div class="login-field" :class="{ 'locked-field-wrapper': isLockedGender, 'input-required': isGenderRequired }" style="position:relative;">
            <img src="@/assets/imgs/icons/venus-mars-solid.svg" alt="Lock Icon" />
            <select
              v-model="currentContact.gender"
              :disabled="isLockedGender"
              :class="{ 'locked-input': isLockedGender, 'input-required': isGenderRequired }"
              style="padding-right:2.2em;padding-left:2.5em;appearance:none;background:transparent;z-index:1;position:relative;"
            >
              <option :value="null">Anrede</option>
              <option value="f">Frau</option>
              <option value="m">Herr</option>
            </select>
            <!-- Custom arrow -->
            <img src="@/assets/imgs/icons/chevron-down-solid.svg" alt="Dropdown" style="position:absolute;right:2.1em;top:50%;transform:translateY(-50%);width:13px;height:13px;pointer-events:none;opacity:0.85;z-index:2;filter:invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.2) contrast(2);" />
            <span v-if="isGenderRequired" style="position:absolute;right:0.5em;top:50%;transform:translateY(-50%);color:#dc2626;font-weight:bold;pointer-events:none;z-index:3;">*</span>
          </div>
          <div class="login-field" :class="{ 'locked-field-wrapper': isLockedFirstName, 'input-required': isFirstNameRequired }">
            <img src="@/assets/imgs/icons/user-solid.svg" alt="Person Icon" />
            <div style="position:relative; width:100%;">
              <input
                v-model="currentContact.firstName"
                type="text"
                class="login-input"
                :readonly="isLockedFirstName"
                :class="{ 'locked-input': isLockedFirstName, 'input-required': isFirstNameRequired }"
                :placeholder="'Vorname'"
                style="padding-right: 1.2em;"
              />
              <span v-if="isFirstNameRequired" style="position:absolute;right:0.5em;top:50%;transform:translateY(-50%);color:#dc2626;font-weight:bold;pointer-events:none;">*</span>
            </div>
          </div>
          <div class="login-field locked-field-wrapper" :class="{ 'input-required': isLastNameRequired }">
            <img src="@/assets/imgs/icons/user-solid.svg" alt="Person Icon" />
            <div style="position:relative; width:100%;">
              <input
                v-model="currentContact.lastName"
                type="text"
                class="login-input"
                :readonly="isLockedLastName"
                :class="{ 'locked-input': isLockedLastName, 'input-required': isLastNameRequired }"
                :placeholder="'Nachname'"
                style="padding-right: 1.2em;"
              />
              <span v-if="isLastNameRequired" style="position:absolute;right:0.5em;top:50%;transform:translateY(-50%);color:#dc2626;font-weight:bold;pointer-events:none;">*</span>
            </div>
          </div>
        </div>
      </div>
      <span v-if="isFirstNameRequired" class="input-error input-error-below" style="width: max-content;">Vorname ist ein Pflichtfeld.</span>
      <!-- Hinweis entfernt: Anrede, Vorname und Nachname sind für bestehende Entscheider gesperrt. -->
      <p v-if="editMode && currentContact && currentContact.id" class="dm-locked-hint" style="margin-top: 0.3rem;">
        <b>Die Felder Anrede, Vorname und Nachname sind immer gesperrt.</b><br> 
        <i>Fehlen Anrede, Vorname oder Nachname, bitte den Entscheider neu anlegen. Bereits gespeicherte Kontaktpersonen dürfen nicht geändert werden, da diese real existieren.</i>
      </p>
      <div class="input-wrapper">
        <div class="login-field">
          <img src="@/assets/imgs/icons/phone-solid.svg" alt="Lock Icon" />
          <input class="login-input" v-model="currentContact.phoneStationary" placeholder="Telefon" />
        </div>
        <div class="login-field">
          <img src="@/assets/imgs/icons/mobile-screen-button-solid.svg" alt="Lock Icon" />
          <input class="login-input" v-model="currentContact.phoneMobile" placeholder="Handynummer" />
          <span v-if="phoneError" class="input-error">{{ phoneError }}</span>
        </div>
        <div class="login-field" :class="{ 'input-required': isEmailRequired }">
          <img src="@/assets/imgs/icons/envelope-solid.svg" alt="Lock Icon" />
          <div style="position:relative; width:100%;">
            <input class="login-input email-fullwidth" v-model="currentContact.email" :placeholder="'E-Mail'" style="padding-right: 1.2em;" />
            <span v-if="isEmailRequired" style="position:absolute;right:0.5em;top:50%;transform:translateY(-50%);color:#dc2626;font-weight:bold;pointer-events:none;">*</span>
          </div>
        </div>
      </div>
      <div v-if="emailError" class="input-error input-error-below">{{ emailError }}</div>
      <div v-if="editMode && !isExistingContact.value" style="margin-top:0.5em;font-size:0.85em;color:#dc2626;font-weight:600;">
        * ist ein Pflichtfeld
      </div>
    </div>
      <template v-else-if="!contacts || contacts.length === 0 || !currentContact">
        <p class="input-error input-error-below" style="margin-top:1rem;max-width:400px;">Es muss mindestens ein Entscheider hinzugefügt werden, um Terminbestätigungen zu versenden.</p>
      </template>

    <div v-if="contacts && contacts.length > 0 && currentContact && !editMode">
      <div class="contactInfoWrapper noEdit">
        <div class="dm-meta">
          <span v-if="currentContact?.id === newestContactId" class="dm-pill dm-new inline">Neu</span>
          <span v-if="currentContact?.main === true" class="dm-pill dm-main inline">Haupt</span>
          <span v-if="!currentContact?.gender || !currentContact?.firstName || !currentContact?.lastName || !currentContact?.email" class="dm-pill dm-unvollstaendig inline">Unvollständig</span>
          <span class="dm-date" v-if="currentContact?.dateCreated">Erstellt: {{ displayDate(currentContact.dateCreated) }}</span>
        </div>
        <div class="input-wrapper threeColumns">
          <div class="inputRow">
            <div class="login-field">
              <img src="@/assets/imgs/icons/venus-mars-solid.svg" alt="Lock Icon" />
              <input v-if="currentContact?.gender==='f'" readonly type="text" class="login-input" placeholder="Frau" />
              <input v-if="currentContact?.gender==='m'" readonly type="text" class="login-input" placeholder="Herr" />
              <input v-if="currentContact?.gender===null" readonly type="text" class="login-input" placeholder="--" />
              <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg" /></button>
            </div>
            <div class="login-field">
              <img src="@/assets/imgs/icons/user-solid.svg" alt="Person Icon" />
              <input readonly v-model="currentContact.firstName" type="text" class="login-input" placeholder="Vorname" />
              <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg" /></button>
            </div>
            <div class="login-field">
              <img src="@/assets/imgs/icons/user-solid.svg" alt="Person Icon" />
              <input readonly v-model="currentContact.lastName" type="text" class="login-input" placeholder="Name" />
              <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg" /></button>
            </div>
          </div>
        </div>
        <div class="input-wrapper">
          <a v-if="currentContact?.phoneStationary" :href="`tel:${currentContact.phoneStationary}`">
            <div class="login-field">
              <img src="@/assets/imgs/icons/phone-solid.svg" alt="Lock Icon" />
              <input readonly class="login-input" v-model="currentContact.phoneStationary" placeholder="Telefon" />
              <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg" /></button>
            </div>
          </a>
          <div v-else class="login-field">
            <img src="@/assets/imgs/icons/phone-solid.svg" alt="Lock Icon" />
            <input readonly class="login-input" v-model="currentContact.phoneStationary" placeholder="Telefon" />
            <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg" /></button>
          </div>

          <div class="login-field" v-if="currentContact?.phoneMobile">
            <a :href="`tel:${currentContact.phoneMobile}`">
              <img src="@/assets/imgs/icons/mobile-screen-button-solid.svg" alt="Lock Icon" />
              <input readonly class="login-input" v-model="currentContact.phoneMobile" placeholder="Handynummer" />
            </a>
            <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg" /></button>
          </div>
          <div v-else class="login-field">
            <img src="@/assets/imgs/icons/mobile-screen-button-solid.svg" alt="Lock Icon" />
            <input readonly class="login-input" v-model="currentContact.phoneMobile" placeholder="Handynummer" />
            <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg" /></button>
          </div>

          <div class="login-field" v-if="currentContact?.email">
            <a :href="`mailto:${currentContact.email}`">
              <img src="@/assets/imgs/icons/envelope-solid.svg" alt="Lock Icon" />
              <input readonly class="login-input" v-model="currentContact.email" placeholder="E-Mail" />
            </a>
            <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg" /></button>
          </div>
          <div v-else class="login-field">
            <img src="@/assets/imgs/icons/envelope-solid.svg" alt="Lock Icon" />
            <input readonly class="login-input" v-model="currentContact.email" placeholder="E-Mail" />
            <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg" /></button>
          </div>
        </div>
      </div>
    </div>
    </div>
 </template>

<style lang="scss" scoped>
select {
  border-radius: 8px;
  padding: 10px;
  font-weight: 700;
  color: #030303;
}
  .prominent-form {
    box-shadow: 0 0 0 4px rgba(239, 125, 0, 0.18);
    border: 2px solid var(--sellwerk-color-main-accent);
    background: linear-gradient(135deg, rgba(255, 250, 240, 0.98), rgba(255, 244, 214, 0.98));
    padding: 1.2rem 1.2rem;
    border-radius: 16px;
    margin-bottom: 1rem;
    transition: box-shadow 0.2s, border 0.2s, background 0.2s;
  }
  .input-error {
    color: #dc2626;
    font-size: 0.8rem;
    margin-left: 0px;
    font-weight: 600;
    background: #fee2e2;
    border-radius: 4px;
    padding: 2px 8px;
    display: inline-block;
  }
  .dm-date {
      position: relative;
      font-style: italic;
      // font-weight: fontsVariables.$opensans-weight-semibold;
      padding-left: 24px;
      &::before {
          content: url('@/assets/imgs/icons/clock-solid.svg');
          position: absolute;
          left: 6px;
          top: 0;
          width: 12px;
          filter: invert(50%) sepia(93%) saturate(2082%) hue-rotate(138deg) brightness(85%) contrast(101%);
      }
  }
  .login-input {
    border-radius: 8px;
  }
  .input-required {
    border: 2px solid #dc2626 !important;
    background: #fee2e2 !important;
  }
  .input-required {
    .input-required {
      border: 0px solid #dc2626 !important;
    }
    border: 2px solid #dc2626 !important;
    background: #fee2e2 !important;
  }
  .email-fullwidth {
    width: 100% !important;
    min-width: 0;
    flex: 1 1 100%;
    box-sizing: border-box;
  }
  .input-error-below {
    margin-top: 2px;
    margin-bottom: 2px;
    margin-left: 0px;
    display: block;
    font-size: 0.92em;
    color: #f87171;
    font-weight: 600;
  }
.dm-card { position:relative; border:1px solid #334155; border-radius:12px; padding:.6rem .7rem; background:linear-gradient(145deg,#1f2731,#1b2229); }
.theme--light .dm-card { background:linear-gradient(145deg,#ffffff,#f5f7fa); border-color:#d5dde5; }
.dm-header { display:flex; flex-direction: column; align-items:left; justify-content:space-between; gap:.6rem; margin-bottom:.5rem; }
.dm-title { display:flex; align-items:center; gap:.5rem; }
.dm-title h4 { margin:0; font-size:.9rem; letter-spacing:.4px; }
.dm-count { font-size:.65rem; font-weight:700; padding:.2rem .5rem; border-radius:999px; background:#0f172a; color:#e2e8f0; }
.theme--light .dm-count { background:#e2e8f0; color:#0f172a; }
.dm-actions { display:flex; align-items:center; gap:.4rem; }
.dm-actions-sep { width:1px; height:22px; background:rgba(148,163,184,.35); margin:0 .2rem; }
.btn { all:unset; cursor:pointer; padding:.35rem .6rem; border-radius:8px; font-size:.7rem; font-weight:700; letter-spacing:.3px; background:rgba(255,255,255,.08); color:#e5e7eb; border:1px solid rgba(148,163,184,.35); }
.btn:hover { background:rgba(255,255,255,.12); }
.btn:disabled { opacity:.5; cursor:default; }
.btn.primary { background:linear-gradient(135deg,#2563eb,#1d4ed8); border-color:rgba(37,99,235,.6); color:#fff; }
.btn.subtle { background:transparent; border-color:rgba(148,163,184,.35); color:#e5e7eb; }
.theme--light .btn { background:rgba(0,0,0,.04); color:#0f172a; border-color:rgba(0,0,0,.08); }
.theme--light .btn:hover { background:rgba(0,0,0,.06); }
.icon-btn { all:unset; cursor:pointer; width:30px; height:30px; display:inline-flex; align-items:center; justify-content:center; border-radius:8px; border:1px solid rgba(148,163,184,.35); background:rgba(255,255,255,.08); }
.icon-btn img { width:14px; height:14px; filter:drop-shadow(0 0 1px rgba(0,0,0,.35)); }
.icon-btn:disabled { opacity:.5; cursor:default; }
.theme--light .icon-btn { background:rgba(0,0,0,.04); }

/* Meta and pill */
.dm-meta { display:flex; align-items:center; gap:.5rem; margin:.25rem 0 .4rem; }
.dm-pill { font-size:.6rem; font-weight:800; padding:.15rem .45rem; border-radius:999px; border:1px solid rgba(245,158,11,.45); background:linear-gradient(135deg,#f59e0b,#d97706); color:#fff; }
.theme--light .dm-pill { background:linear-gradient(135deg,#fed7aa,#fdba74); color:#1e293b; border-color:#fdba74; }
.dm-pill.inline { margin-right:.25rem; }
.dm-new { letter-spacing:.3px; text-transform:uppercase; }
.dm-date { font-size:.7rem; color:#cbd5e1; }
.theme--light .dm-date { color:#475569; }
.dm-pill.dm-unvollstaendig {
  background: linear-gradient(135deg,#ff5d47,#d91f06);
  color: #fff;
}

/* Loading overlay */
.dm-loading { position:absolute; inset:0; z-index:15; display:flex; align-items:center; justify-content:center; background:rgba(0,0,0,.25); backdrop-filter: blur(2px) saturate(160%); border-radius:12px; }
.theme--light .dm-loading { background:rgba(255,255,255,.6); }
.dm-loader { width:32px; height:32px; border:4px solid rgba(255,255,255,.35); border-top-color:#f59e0b; border-radius:50%; animation: dmSpin 1s linear infinite; }
.theme--light .dm-loader { border-color:rgba(0,0,0,.2); border-top-color:#f59e0b; }
@keyframes dmSpin { to { transform: rotate(360deg); } }

/* Inputs alignment tweaks */
.contactInfoWrapper.editing-mode .input-wrapper { display:grid; gap:.5rem; }
.login-field { display:flex; align-items:center; gap:.4rem;  }
.login-field input, .login-field select { flex:1; height: 100%; }
.login-field button { all:unset; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; width:28px; height:28px; border-radius:6px; border:1px solid rgba(148,163,184,.35); background:rgba(255,255,255,.08); }
.theme--light .login-field button { background:rgba(0,0,0,.04); }

/* Visuelle Sperre für Stammdatenfelder (Geschlecht, Vorname, Nachname) */
.locked-field-wrapper {
  position: relative;
}

.locked-input {
  background: rgba(248, 113, 113, 0.08) !important; /* leichtes Rot */
  border-color: rgba(239, 68, 68, 0.9) !important;
  color: #fee2e2 !important;
  // box-shadow: 0 0 0 1px rgba(248, 113, 113, 0.7);
  cursor: not-allowed;
}
.login-field.locked-field-wrapper {
  // background: rgba(248, 113, 113, 0.08) !important;
  // // border-color: rgba(239, 68, 68, 0.9) !important;
  // box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.25) !important;
}
.theme--light .locked-input {
  color: #7f1d1d !important;
}

.dm-locked-hint {
  margin-left: 0.3rem;
  font-size: 0.7rem;
  color: #fecaca;
}
.theme--light .dm-locked-hint {
  color: #b91c1c;
}

/* Responsive layout for actions */
@media (max-width: 920px) {
  .dm-header { flex-direction: column; align-items: stretch; gap:.5rem; }
  .dm-title { justify-content: space-between; }
  .dm-actions { flex-wrap: wrap; justify-content: flex-start; }
  .dm-actions .btn, .dm-actions .icon-btn { flex: 0 0 auto; }
}
@media (max-width: 640px) {
  .btn { font-size:.68rem; padding:.32rem .55rem; }
  .icon-btn { width:34px; height:34px; }
  .dm-count { font-size:.6rem; }
}
</style>
